import React, { useMemo, useState, useEffect, useCallback } from "react";
import { PropTypes } from "prop-types";
import { Box, Button } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import { intersectionBy, differenceBy, sortBy } from "lodash";
import { usePositions } from "../../hooks/positions";
import SearchBar from "../../components/SearchBar/SearchBar";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import DualListContainer from "../../components/DualList/DualListContainer";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { buildGroupDetailInformationRoute } from "../../routes/AdminRoutes";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../redux/actions/snackbarActions";
import useUser from "../../hooks/useUser";

const ManageGroupPositionsFilters = ({ onSearch }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState("");

  const handleApplyFilters = newSearchValue => {
    setSearchValue(newSearchValue);
    onSearch(newSearchValue);
  };

  return (
    <Box display="flex" alignItems="center">
      <Box flex={1} mr={3}>
        <SearchBar
          onSearchSubmit={handleApplyFilters}
          filled
          searchIconPosition="end"
          background={theme.palette.inputBackground}
          placeholder={t("manageGroupPositions.searchLabel")}
          value={searchValue}
          onSearchInput={setSearchValue}
          controlled
        />
      </Box>
      <ButtonGroup>
        <Button
          color="primary"
          variant="text"
          onClick={() => handleApplyFilters("")}
          data-qa="manage-group-positions-clear-filters-btn"
        >
          {t("clearAllBtn")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => onSearch(searchValue)}
          data-qa="manage-group-positions-apply-filters-btn"
        >
          {t("filterList")}
        </Button>
      </ButtonGroup>
    </Box>
  );
};

const ManageGroupPositions = ({
  groupPositions,
  groupName,
  groupId,
  divisionId,
  onSave
}) => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const { getPositions, isLoading: isLoadingPositions } = usePositions();
  const dispatch = useDispatch();
  const { isClient } = useUser();
  const [filters, setFilters] = useState({ searchValue: "" });
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);

  useEffect(() => {
    groupId &&
      getPositions(
        divisionId ? { filters: { divisionId: divisionId } } : {}
      ).then(positions => setPositions(positions.data));
  }, [groupId]);

  const sortPositions = positions =>
    sortBy(positions, [position => position.name.toLowerCase()]);

  const assignedPositions = useMemo(
    () => sortPositions(intersectionBy(positions, groupPositions, "id")),
    [positions, groupPositions]
  );
  const unassignedPositions = useMemo(
    () => sortPositions(differenceBy(positions, groupPositions, "id")),
    [positions, groupPositions]
  );

  const filterPositions = useCallback(
    positions =>
      sortPositions(
        positions.filter(position =>
          position.name
            ?.toLowerCase()
            .includes(filters.searchValue.toLowerCase())
        )
      ),
    [filters]
  );

  const handleSearch = searchValue => {
    setFilters({ searchValue });
  };

  const getListItemDetails = item => {
    const divisionLine = `${item.customer?.name}`;
    return {
      id: item.id,
      name: item.name,
      subtitles: isClient && [divisionLine]
    };
  };

  const getUpdatedGroupPositions = (initialList, newList) => {
    const updatedPositions = differenceBy(newList, initialList, "id");
    return updatedPositions.map(({ id: positionId }) => ({
      position: { type: "position", id: positionId },
      group: { type: "group", id: groupId }
    }));
  };

  const handleSave = (unselectedItems, selectedItems) => {
    const groupPositionsToCreate = getUpdatedGroupPositions(
      assignedPositions,
      selectedItems
    );
    const groupPositionsToDelete = getUpdatedGroupPositions(
      unassignedPositions,
      unselectedItems
    );
    const positionsWereUpdated =
      groupPositionsToCreate.length > 0 || groupPositionsToDelete.length > 0;
    if (positionsWereUpdated) {
      setIsUpdatingPositions(true);

      onSave(groupPositionsToCreate, groupPositionsToDelete).then(response => {
        setIsUpdatingPositions(false);

        if (response.errors) {
          dispatch(
            enqueueErrorSnackbar({
              message: t("manageGroupPositions.errorMsg")
            })
          );
        } else {
          dispatch(
            enqueueSuccessSnackbar({
              message: t(
                "administration.groupInformation.manageGroupSuccessMsg"
              ),
              options: {
                autoHideDuration: 10000
              }
            })
          );
        }
      });
    }
  };
  const isLoading = !groupId || isLoadingPositions;
  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={[
            t(`administration.groups`),
            groupName,
            t("manageGroupPositions.title")
          ]}
        />
      }
      onGoBackClick={() =>
        dispatch(push(buildGroupDetailInformationRoute(groupId)))
      }
    >
      <Box mt={3} flex={1} display="flex">
        <DualListContainer
          initialSelectedItems={assignedPositions}
          initialUnselectedItems={unassignedPositions}
          filters={<ManageGroupPositionsFilters onSearch={handleSearch} />}
          filterUnselected={filterPositions}
          estimatedItemSize={69}
          rowHeight={69}
          getListItemDetails={getListItemDetails}
          unselectedListHeader={t("manageGroupPositions.availablePositions")}
          selectedListHeader={t("manageGroupPositions.groupPositions")}
          onSave={handleSave}
          saveButtonText={t("manageGroupPositions.saveBtn")}
          isLoadingItems={isLoading}
          disabled={isLoading || isUpdatingPositions}
          isSaving={isUpdatingPositions}
        />
      </Box>
    </AdminPageLayout>
  );
};

export default ManageGroupPositions;

ManageGroupPositions.propTypes = {
  groupPositions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  groupName: PropTypes.string,
  groupId: PropTypes.string,
  onSave: PropTypes.func
};
